import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from "@/components/ui/input";
import Image from "next/image";
import { IDraftInputs } from '@/lib/interfaces';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { getTokensCost } from './actions';
import HttpStatus from '@/lib/HttpStatus';
import CartaTokens from '@/../public/images/tokens_sm.png';

interface INewDraftInput {
  isMyLoading: boolean,
  draft: IDraftInputs,
  handleIsProfane: (text: string) => string,
  handleGenerate: () => Promise<void>,
  setDraft: Dispatch<SetStateAction<IDraftInputs>> //(draft: IDraftInputs) => void
}

function DraftDetailsSection({ isMyLoading, draft, handleIsProfane, handleGenerate, setDraft }: INewDraftInput) {
  const [ generationCost, setGenerationCost ] = useState<number>(0);

  useEffect(() => {
    async function getCost() {
      try {
        const cost = await getTokensCost();

        if(cost.status != HttpStatus.OK) throw new Error(cost.error!);

        setGenerationCost(cost.data);

      } catch(error) {
        setGenerationCost(0);
      }
    }

    getCost();
  }, []);

  return (
    <Card className="shadow-xl dark:bg-gray-800">
      <CardHeader className="border-b dark:border-gray-700">
        <CardTitle className="text-xl text-indigo-600">Context & Tone</CardTitle>
        <CardDescription>Define the relationship and what you need to communicate.</CardDescription>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        <div className="space-y-2">
          <Label htmlFor="title" className='text-indigo-600'>Draft Title</Label>
          <Input type="text" placeholder='Draft Title...' value={draft.title}
            onChange={(e) => {
              let title = handleIsProfane(e.currentTarget.value);
              setDraft(prev => ({...prev, title}))
            }} disabled={isMyLoading} />
        </div>
        
        {/* User Role Input */}
        <div className="space-y-2">
          <Label htmlFor="userRole" className='text-indigo-600'>Your Role (Sender)</Label>
          <Input type="text" placeholder='Your Role (Sender)...' value={draft.userRole}
            onChange={(e) => {
              let userRole = handleIsProfane(e.currentTarget.value);
              setDraft(prev => ({...prev, userRole}))
            }} disabled={isMyLoading} />
        </div>

        {/* Recipient Role Input */}
        <div className="space-y-2">
          <Label htmlFor="recipientRole" className='text-indigo-600'>Recipient's Role</Label>
          <Input type="text" placeholder='Recipient Role...' value={draft.recipientRole}
            onChange={(e) => {
              let recipientRole = handleIsProfane(e.currentTarget.value);
              setDraft(prev => ({...prev, recipientRole}))
            }} disabled={isMyLoading} />
        </div>

        {/* Tone Input */}
        <div className="space-y-2">
          <Label htmlFor="tone" className='text-indigo-600'>Tone of the Email</Label>
          <Select onValueChange={(value) => setDraft(prev => ({...prev, tone: value}))} disabled={isMyLoading}>
            <SelectTrigger id="tone">
              <SelectValue placeholder="Select the desired tone" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Professional">Professional</SelectItem>
              <SelectItem value="Friendly and Casual">Friendly and Casual</SelectItem>
              <SelectItem value="Urgent and Direct">Urgent and Direct</SelectItem>
              <SelectItem value="Formal and Serious">Formal and Serious</SelectItem>
              <SelectItem value="Apologetic">Apologetic</SelectItem>
              <SelectItem value="Enthusiastic">Enthusiastic</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Details of the email */}
        <div className="space-y-2">
          <Label htmlFor="details" className='text-indigo-600'>Email Details (Key Information/Request)</Label>
          <Textarea
            id="details"
            placeholder="e.g., Confirming the next meeting is Friday at 2 PM regarding the Q4 budget. We need to discuss the new marketing plan."
            value={draft.details}
            onChange={(e) => {
              const details = handleIsProfane(e.currentTarget.value);
              setDraft(prev => ({...prev, details}))}
            }
            rows={7}
            disabled={isMyLoading}
          />
        </div>

        {/* Generate Button (with loading state) */}
        <Button 
            onClick={handleGenerate} 
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white transition duration-200"
            disabled={isMyLoading}
        >
            {isMyLoading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Drafting Email...</>
            ) : (
              <div className="flex justify-center items-center gap-2">
                <div>Generate Email Draft</div>
                <div className="flex justify-center items-center text-sm">
                  <span>{"("}</span><Image src={CartaTokens} width={20} height={20} alt="Carta AI Tokens" />
                  <span className="ml-1">{generationCost} <span className="hidden md:inline">tokens</span></span><span>{")"}</span>
                </div>
              </div>
                
            )}
        </Button>
      </CardContent>
    </Card>  )
}

export default DraftDetailsSection